import { getApps, initializeApp, type FirebaseApp } from 'firebase/app'
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth'
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore'
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions'
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage'
import { readFirebaseEnv } from './env'

/** Mesma região das Functions no servidor — `functions/src/region.ts`. Os dois
 * pacotes não se alcançam (`functions/` tem `rootDir: src` próprio), então o
 * literal existe dos dois lados. Se um mudar, o outro muda junto: uma callable
 * chamada na região errada devolve `not-found`, não um erro que se explique. */
const FUNCTIONS_REGION = 'southamerica-east1'

export type FirebaseServices = {
  app: FirebaseApp
  auth: Auth
  db: Firestore
  functions: Functions
  storage: FirebaseStorage
}

let services: FirebaseServices | null = null

const EMULATORS_KEY = '__porquinhoEmulatorsConnected'

/**
 * Conecta os emuladores uma vez só por app do Firebase.
 *
 * A marca vai no **app**, não no módulo. No HMR o módulo reavalia e `services`
 * volta a `null`, mas `getApps()[0]` devolve o app anterior, já com Firestore
 * iniciado — e aí `connectFirestoreEmulator` lança `Firestore has already been
 * started`. Um guard guardado no módulo não sobrevive à reavaliação do próprio
 * módulo; um guard guardado no objeto que sobrevive, sim.
 */
function connectEmulatorsOnce(
  app: FirebaseApp,
  auth: Auth,
  db: Firestore,
  functions: Functions,
  storage: FirebaseStorage,
) {
  const marked = app as FirebaseApp & { [EMULATORS_KEY]?: boolean }
  if (marked[EMULATORS_KEY]) return
  marked[EMULATORS_KEY] = true
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
  connectFunctionsEmulator(functions, '127.0.0.1', 5001)
  connectStorageEmulator(storage, '127.0.0.1', 9199)
}

/**
 * Em DEV, contra os emuladores — a menos que `VITE_USE_EMULATORS=false`.
 *
 * O padrão é emulador porque é o que se quer quase sempre: rápido, descartável,
 * e não escreve nada num projeto de verdade. Mas o login com Google do emulador
 * é um seletor de contas falsas — ele não exercita OAuth, nem domínio
 * autorizado, nem a blocking function. Para testar *isso* localmente, existe a
 * saída.
 *
 * O build de produção nunca chega aqui: `import.meta.env.DEV` é `false` e o
 * Vite remove o ramo inteiro do bundle.
 */
function shouldUseEmulators(): boolean {
  if (!import.meta.env.DEV) return false
  return import.meta.env.VITE_USE_EMULATORS !== 'false'
}

/** Inicializa uma vez. Em DEV conecta nos emuladores, salvo opt-out. */
export function getFirebase(): FirebaseServices {
  if (services) return services

  const config = readFirebaseEnv()
  const app = getApps()[0] ?? initializeApp(config)
  const auth = getAuth(app)
  const db = getFirestore(app)
  const functions = getFunctions(app, FUNCTIONS_REGION)
  const storage = getStorage(app)

  if (shouldUseEmulators()) {
    connectEmulatorsOnce(app, auth, db, functions, storage)
  } else if (import.meta.env.DEV) {
    // Alto de propósito. Rodar local contra projeto de verdade escreve dado de
    // verdade, e confundir os dois modos é como se escreve em produção achando
    // que está brincando. O nome do projeto vai junto para não restar dúvida.
    console.warn(
      `[porquinho] Emuladores DESLIGADOS. Falando com o projeto "${config.projectId}" de verdade. ` +
        'Tudo que este app escrever fica lá.',
    )
  }

  services = { app, auth, db, functions, storage }
  return services
}
